import React, { useEffect, useState } from "react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { Edit3, FileQuestion, ImagePlus, Plus, Power, RefreshCw, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const empty = {
  slug: "", title: "", description: "", energy_cost: 0, duration: "", category: "general",
  coverImageUrl: "", requirements: [], intake_fields: [], material_requirements: [], deliverables: [],
  featured: false, landing_visible: true, icon: "✦", tone: "#D4AF37", active: true,
};
const nextId = (prefix, rows) => `${prefix}_${rows.length + 1}`;
const fieldTypes = [
  ["short_text", "Texto corto"], ["long_text", "Respuesta amplia"], ["number", "Número"],
  ["date", "Fecha"], ["yes_no", "Sí / No"], ["choice", "Opciones"],
];
const materialTypes = [
  ["image", "Fotografía / imagen"], ["document", "Documento"], ["audio", "Audio"],
  ["video", "Video"], ["any", "Cualquier archivo permitido"],
];

const DiagnosticsCatalog = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    let active = true;
    api.get("/api/director/diagnostics/catalog")
      .then(({ data }) => { if (active) { setItems(data); setError(""); } })
      .catch((e) => { if (active) setError(formatApiError(e)); });
    return () => { active = false; };
  }, [version]);

  const reload = () => setVersion((value) => value + 1);
  const startNew = () => { setForm({ ...empty }); setEditing("new"); };
  const startEdit = (item) => {
    setForm({
      ...empty,
      ...item,
      coverImageUrl: item.coverImageUrl || item.cover_url || "",
      intake_fields: item.intake_fields || [],
      material_requirements: item.material_requirements || [],
    });
    setEditing(item.slug);
  };
  const updateField = (index, patch) => setForm((current) => ({
    ...current,
    intake_fields: current.intake_fields.map((row, i) => i === index ? { ...row, ...patch } : row),
  }));
  const updateMaterial = (index, patch) => setForm((current) => ({
    ...current,
    material_requirements: current.material_requirements.map((row, i) => i === index ? { ...row, ...patch } : row),
  }));

  const uploadCover = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setSaved("");
      setError("Formato no permitido. Selecciona una imagen JPG, PNG o WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSaved("");
      setError("La imagen supera el límite de 5 MB.");
      return;
    }

    const data = new FormData();
    data.append("file", file);
    setUploadingCover(true);
    setError("");
    setSaved("");
    try {
      const response = await api.post("/api/uploads/diagnostic-cover", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((current) => ({ ...current, coverImageUrl: response.data.coverImageUrl }));
      setSaved("Imagen de portada subida correctamente.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setUploadingCover(false);
    }
  };

  const removeCover = () => {
    setForm((current) => ({ ...current, coverImageUrl: "" }));
    setError("");
    setSaved("Imagen de portada eliminada del diagnóstico. Guarda para confirmar el cambio.");
  };

  const save = async () => {
    setError(""); setSaved("");
    if (!form.slug.trim() || !form.title.trim()) return setError("Slug y título son obligatorios.");
    const energyCost = Number(form.energy_cost);
    if (!Number.isInteger(energyCost) || energyCost < 0 || energyCost > 1000000) {
      return setError("El costo debe ser una cantidad entera de Energía KD9 entre 0 y 1.000.000.");
    }
    setSaving(true);
    const payload = {
      ...form,
      energy_cost: energyCost,
      coverImageUrl: form.coverImageUrl || null,
      intake_fields: form.intake_fields.map((row) => ({ ...row, options: row.type === "choice" ? row.options : [] })),
      material_requirements: form.material_requirements.map((row) => ({
        ...row, min_files: Number(row.min_files), max_files: Number(row.max_files),
      })),
    };
    try {
      if (editing === "new") await api.post("/api/director/diagnostics/catalog", payload);
      else await api.put(`/api/director/diagnostics/catalog/${editing}`, payload);
      setEditing(null); setSaved("Diagnóstico, preguntas y material requerido guardados."); reload();
    } catch (e) { setError(formatApiError(e)); } finally { setSaving(false); }
  };

  const toggle = (slug) => api.post(`/api/director/diagnostics/catalog/${slug}/toggle`).then(reload).catch((e) => setError(formatApiError(e)));
  const archiveItem = (slug) => window.confirm("¿Archivar este diagnóstico? Se retirará del landing y del catálogo público.") && api.post(`/api/director/diagnostics/catalog/${slug}/archive`).then(reload).catch((e)=>setError(formatApiError(e)));
  const restoreItem = (slug) => api.post(`/api/director/diagnostics/catalog/${slug}/restore`).then(reload).catch((e)=>setError(formatApiError(e)));
  const removeItem = (slug) => window.confirm("Eliminar definitivamente solo es posible si nunca tuvo solicitudes. ¿Continuar?") && api.delete(`/api/director/diagnostics/catalog/${slug}`).then(reload).catch((e)=>setError(formatApiError(e)));
  const fmtEnergy = (n) => `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(n || 0)} Energía KD9`;

  return <PageShell kicker="Director KD9 · Catálogo" title="GESTIÓN DE DIAGNÓSTICOS"
    desc="Define el costo en energía, las preguntas y la cantidad exacta de fotos o archivos que deberá entregar el usuario."
    testId="dir-diagnostics-catalog-page"
    badge={<button onClick={reload} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-2"><RefreshCw size={12}/> Recargar</button>}>
    {saved && <div className="kd9-card p-4 mb-4 text-sm" style={{color:"#22C55E"}}>{saved}</div>}
    {error && <div className="kd9-card p-4 mb-4 text-sm" style={{color:"#FCA5A5"}}>{error}</div>}

    {editing ? <div className="kd9-card p-6 mb-6">
      <div className="font-cinzel kd9-gold-text text-lg mb-4">{editing === "new" ? "NUEVO DIAGNÓSTICO" : `EDITAR · ${editing}`}</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-xs">Identificador
          <input className="kd9-input w-full mt-1 p-3" value={form.slug} disabled={editing !== "new"} onChange={(e)=>setForm({...form,slug:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")})}/>
        </label>
        <label className="text-xs">Título
          <input className="kd9-input w-full mt-1 p-3" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})}/>
        </label>
        <label className="text-xs sm:col-span-2">Descripción
          <textarea rows={3} className="kd9-input w-full mt-1 p-3" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})}/>
        </label>
        <label className="text-xs">Costo en energía
          <input type="number" min="0" max="1000000" step="1" inputMode="numeric" className="kd9-input w-full mt-1 p-3" value={form.energy_cost} onChange={(e)=>setForm({...form,energy_cost:e.target.value})}/>
          <span className="block mt-1" style={{color:"var(--kd9-text-soft)"}}>Usa Energía KD9 entera; no se permiten fracciones.</span>
        </label>
        <label className="text-xs">Tiempo de entrega
          <input className="kd9-input w-full mt-1 p-3" placeholder="Ej. 5 días hábiles" value={form.duration} onChange={(e)=>setForm({...form,duration:e.target.value})}/>
        </label>
        <label className="text-xs">Categoría
          <input className="kd9-input w-full mt-1 p-3" value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})}/>
        </label>
        <div className="text-xs sm:col-span-2">
          <span className="block mb-2">Imagen de portada</span>
          <div className="rounded-xl border p-4" style={{borderColor:"var(--kd9-border)"}}>
            {form.coverImageUrl ? (
              <div className="space-y-3">
                <img
                  src={form.coverImageUrl}
                  alt="Vista previa de la portada del diagnóstico"
                  className="w-full max-w-md h-48 object-cover rounded-lg"
                />
                <div className="flex flex-wrap gap-2">
                  <label className="kd9-btn-outline px-4 py-2 rounded-lg cursor-pointer">
                    {uploadingCover ? "Subiendo…" : "Cambiar imagen"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="sr-only"
                      disabled={uploadingCover}
                      onChange={uploadCover}
                    />
                  </label>
                  <button type="button" onClick={removeCover} disabled={uploadingCover} className="kd9-btn-outline px-4 py-2 rounded-lg">
                    <Trash2 size={14} className="inline mr-1"/> Eliminar
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 min-h-36 cursor-pointer rounded-lg border border-dashed p-5" style={{borderColor:"var(--kd9-border)"}}>
                <ImagePlus size={28}/>
                <strong>{uploadingCover ? "Subiendo imagen…" : "Seleccionar imagen"}</strong>
                <span>JPG, PNG o WebP · máximo 5 MB</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  disabled={uploadingCover}
                  onChange={uploadCover}
                />
              </label>
            )}
          </div>
        </div>
        <label className="text-xs">Requisitos visibles (uno por línea)
          <textarea rows={4} className="kd9-input w-full mt-1 p-3" value={(form.requirements||[]).join("\n")} onChange={(e)=>setForm({...form,requirements:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}/>
        </label>
        <label className="text-xs">Entregables (uno por línea)
          <textarea rows={4} className="kd9-input w-full mt-1 p-3" value={(form.deliverables||[]).join("\n")} onChange={(e)=>setForm({...form,deliverables:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}/>
        </label>
      </div>

      <section className="mt-7">
        <div className="flex justify-between items-center mb-3"><div className="flex gap-2 items-center"><FileQuestion size={16}/><strong>Preguntas del formulario</strong></div>
          <button className="kd9-btn-outline px-3 py-2 rounded-lg text-xs" onClick={()=>setForm({...form,intake_fields:[...form.intake_fields,{id:nextId("pregunta",form.intake_fields),label:"Nueva pregunta",type:"long_text",required:true,help_text:"",options:[]}]})}><Plus size={12} className="inline"/> Pregunta</button>
        </div>
        {form.intake_fields.length === 0 && <div className="text-xs p-4 border rounded-lg" style={{borderColor:"var(--kd9-border)"}}>La consulta principal siempre se solicita. Añade aquí las preguntas específicas de este diagnóstico.</div>}
        <div className="space-y-3">{form.intake_fields.map((row,index)=><div key={index} className="p-4 border rounded-xl grid md:grid-cols-2 gap-3" style={{borderColor:"var(--kd9-border)"}}>
          <label className="text-xs">Pregunta<input className="kd9-input w-full mt-1 p-2" value={row.label} onChange={(e)=>updateField(index,{label:e.target.value})}/></label>
          <label className="text-xs">Identificador interno<input className="kd9-input w-full mt-1 p-2" value={row.id} onChange={(e)=>updateField(index,{id:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"_")})}/></label>
          <label className="text-xs">Tipo<select className="kd9-input w-full mt-1 p-2" value={row.type} onChange={(e)=>updateField(index,{type:e.target.value})}>{fieldTypes.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></label>
          <label className="text-xs">Ayuda para el usuario<input className="kd9-input w-full mt-1 p-2" value={row.help_text||""} onChange={(e)=>updateField(index,{help_text:e.target.value})}/></label>
          {row.type === "choice" && <label className="text-xs md:col-span-2">Opciones (una por línea)<textarea className="kd9-input w-full mt-1 p-2" value={(row.options||[]).join("\n")} onChange={(e)=>updateField(index,{options:e.target.value.split("\n").map(x=>x.trim()).filter(Boolean)})}/></label>}
          <div className="md:col-span-2 flex justify-between"><label className="text-xs flex gap-2"><input type="checkbox" checked={row.required} onChange={(e)=>updateField(index,{required:e.target.checked})}/> Obligatoria</label><button onClick={()=>setForm({...form,intake_fields:form.intake_fields.filter((_,i)=>i!==index)})} aria-label="Eliminar pregunta"><Trash2 size={16}/></button></div>
        </div>)}</div>
      </section>

      <section className="mt-7">
        <div className="flex justify-between items-center mb-3"><div className="flex gap-2 items-center"><ImagePlus size={16}/><strong>Fotos y material solicitado</strong></div>
          <button className="kd9-btn-outline px-3 py-2 rounded-lg text-xs" onClick={()=>setForm({...form,material_requirements:[...form.material_requirements,{id:nextId("material",form.material_requirements),label:"Fotografía requerida",kind:"image",required:true,min_files:1,max_files:1,instructions:""}]})}><Plus size={12} className="inline"/> Material</button>
        </div>
        <div className="space-y-3">{form.material_requirements.map((row,index)=><div key={index} className="p-4 border rounded-xl grid md:grid-cols-2 gap-3" style={{borderColor:"var(--kd9-border)"}}>
          <label className="text-xs">Nombre del material<input className="kd9-input w-full mt-1 p-2" value={row.label} onChange={(e)=>updateMaterial(index,{label:e.target.value})}/></label>
          <label className="text-xs">Identificador interno<input className="kd9-input w-full mt-1 p-2" value={row.id} onChange={(e)=>updateMaterial(index,{id:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"_")})}/></label>
          <label className="text-xs">Tipo<select className="kd9-input w-full mt-1 p-2" value={row.kind} onChange={(e)=>updateMaterial(index,{kind:e.target.value})}>{materialTypes.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></label>
          <label className="text-xs">Instrucciones<input className="kd9-input w-full mt-1 p-2" value={row.instructions||""} onChange={(e)=>updateMaterial(index,{instructions:e.target.value})}/></label>
          <label className="text-xs">Cantidad mínima<input type="number" min="0" max="20" className="kd9-input w-full mt-1 p-2" value={row.min_files} onChange={(e)=>updateMaterial(index,{min_files:e.target.value})}/></label>
          <label className="text-xs">Cantidad máxima<input type="number" min="1" max="20" className="kd9-input w-full mt-1 p-2" value={row.max_files} onChange={(e)=>updateMaterial(index,{max_files:e.target.value})}/></label>
          <div className="md:col-span-2 flex justify-between"><label className="text-xs flex gap-2"><input type="checkbox" checked={row.required} onChange={(e)=>updateMaterial(index,{required:e.target.checked,min_files:e.target.checked && Number(row.min_files)<1?1:row.min_files})}/> Obligatorio</label><button onClick={()=>setForm({...form,material_requirements:form.material_requirements.filter((_,i)=>i!==index)})} aria-label="Eliminar material"><Trash2 size={16}/></button></div>
        </div>)}</div>
      </section>

      <div className="mt-6 flex flex-wrap gap-5">
        <label className="text-xs flex gap-2"><input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})}/> Activo</label>
        <label className="text-xs flex gap-2"><input type="checkbox" checked={form.landing_visible} onChange={(e)=>setForm({...form,landing_visible:e.target.checked})}/> Visible en landing</label>
        <label className="text-xs flex gap-2"><input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form,featured:e.target.checked})}/> Destacado</label>
      </div>
      <div className="mt-5 flex gap-3"><button onClick={save} disabled={saving || uploadingCover} className="kd9-btn-primary px-6 py-3 rounded-lg text-xs">{saving?"Guardando…":"Guardar diagnóstico"}</button><button onClick={()=>setEditing(null)} className="kd9-btn-outline px-6 py-3 rounded-lg text-xs">Cancelar</button></div>
    </div> : <button onClick={startNew} className="kd9-btn-primary px-6 py-3 rounded-lg text-xs flex items-center gap-2 mb-5"><Plus size={14}/> Nuevo diagnóstico</button>}

    <div className="kd9-card overflow-hidden">{items.length===0?<div className="p-8 text-center">Sin diagnósticos.</div>:items.map((it,i)=><div key={it.slug} className="p-4 border-b last:border-0 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-center" style={{borderColor:"var(--kd9-border)"}}>
      <div><strong>{it.title}</strong><div className="text-xs">{it.slug} · {it.duration} · {it.archived?"Archivado":it.active?(it.landing_visible?"Publicado en landing":"Activo fuera del landing"):"Desactivado"}</div></div>
      <div className="text-xs">{fmtEnergy(it.energy_cost)} · {(it.intake_fields||[]).length} preguntas · {(it.material_requirements||[]).length} tipos de material</div>
      <div className="flex gap-2 flex-wrap">{!it.archived&&<><button onClick={()=>startEdit(it)} className="p-2 border rounded-lg" aria-label="Editar"><Edit3 size={14}/></button><button onClick={()=>toggle(it.slug)} className="p-2 border rounded-lg" aria-label="Activar o desactivar"><Power size={14} color={it.active?"#22C55E":"#FCA5A5"}/></button><button onClick={()=>archiveItem(it.slug)} className="p-2 border rounded-lg text-xs">Archivar</button></>}{it.archived&&<button onClick={()=>restoreItem(it.slug)} className="p-2 border rounded-lg text-xs">Restaurar</button>}<button onClick={()=>removeItem(it.slug)} className="p-2 border rounded-lg" aria-label="Eliminar definitivamente"><Trash2 size={14}/></button></div>
    </div>)}</div>
  </PageShell>;
};
export default DiagnosticsCatalog;
